import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { LevelBadge } from "@/components/LevelBadge";
import { UserSearch, ShieldCheck, FlaskConical } from "lucide-react";

const ACT = { taught: "bg-[#C85A32] text-white", reviewed: "bg-[#FEF3C7] text-[#92600A]", danced: "bg-secondary text-[#5C4A42]" };

function MiniTimeline({ data }) {
  if (!data?.length) return <p className="text-sm text-[#6E5B51]">No history.</p>;
  return (
    <div className="space-y-2">
      {data.map((g) => (
        <div key={g.date} className="rounded-xl border border-border bg-card p-2.5">
          <p className="mb-1 text-xs font-bold text-[#B04B26]">{g.date}</p>
          <ul className="space-y-1">
            {g.dances.map((x) => (
              <li key={x.dance.id} className="flex flex-wrap items-center gap-1.5 text-sm">
                <span className="flex-1 font-semibold text-[#2D1E18]">{x.dance.name}</span>
                {(x.activities || []).map((a) => <span key={a} className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase ${ACT[a] || "bg-secondary"}`}>{a}</span>)}
                <LevelBadge level={x.dance.level} />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default function MemberPreviewPanel() {
  const [uid, setUid] = useState("");
  const { data: members } = useQuery({ queryKey: ["admin-users"], queryFn: async () => (await api.get("/admin/users")).data });
  const { data, isFetching } = useQuery({ queryKey: ["member-preview", uid], enabled: !!uid, queryFn: async () => (await api.get(`/admin/us/pilot/member-preview?user_id=${uid}`)).data });
  const memberList = (members || []).filter((m) => m.role === "member");

  return (
    <div className="mb-8 rounded-2xl border-2 border-[#2E7D73]/40 bg-[#F3FAF8] p-4" data-testid="member-preview-panel">
      <div className="mb-3 flex items-center gap-2">
        <UserSearch size={18} className="text-[#1F6E64]" />
        <h2 className="font-display text-lg font-extrabold text-[#2D1E18]">Individual Member Preview (admin-only)</h2>
      </div>
      <p className="mb-3 flex items-start gap-2 rounded-xl bg-[#E2F2EE] px-3 py-2 text-xs text-[#1F5A52]">
        <ShieldCheck size={16} className="mt-0.5 shrink-0" />
        <span>Legacy vs unified dashboard for one member, respecting their real class enrolments. No contact, emergency or personal data is shown. Read-only — nothing is modified. Access is audited.</span>
      </p>

      <div className="max-w-md">
        <label className="mb-1 block text-xs font-bold uppercase text-[#6E5B51]">Member</label>
        <select className="min-h-11 w-full rounded-xl border border-border bg-card px-3 py-2 text-sm" value={uid} onChange={(e) => setUid(e.target.value)} data-testid="member-preview-select">
          <option value="">Select a member…</option>
          {memberList.map((m) => <option key={m.id} value={m.id}>{m.first_name || m.name || m.email?.split("@")[0]} {m.last_name || ""}</option>)}
        </select>
      </div>

      {uid && (isFetching ? <p className="mt-4 text-sm text-[#6E5B51]">Loading…</p> : data && (
        <div className="mt-4 space-y-5" data-testid="member-preview-body">
          <p className="text-sm text-[#2D1E18]" data-testid="member-preview-name">
            <strong>{data.member.name}</strong> · {data.followed_classes.length} class{data.followed_classes.length !== 1 ? "es" : ""} followed
            {data.pilot_classes.length > 0 && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#E7DBF2] px-2 py-0.5 text-[10px] font-bold uppercase text-[#6B3E86]"><FlaskConical size={11} /> {data.pilot_classes.length} in pilot</span>}
          </p>

          <div>
            <h3 className="mb-2 font-display font-extrabold text-[#2D1E18]">Dashboard "New Dances"</h3>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="rounded-xl border border-border bg-card p-3" data-testid="mp-nd-old">
                <p className="mb-2 text-xs font-bold uppercase text-[#6E5B51]">Legacy</p>
                {data.new_dances.old.length ? <ul className="space-y-1 text-sm">{data.new_dances.old.map((d) => <li key={d.id} className="font-semibold text-[#2D1E18]">{d.name} <span className="text-xs text-[#6E5B51]">· {d.taught_at?.class_name}</span></li>)}</ul> : <p className="text-sm text-[#6E5B51]">None</p>}
              </div>
              <div className="rounded-xl border border-[#2E7D73] bg-card p-3" data-testid="mp-nd-new">
                <p className="mb-2 text-xs font-bold uppercase text-[#1F6E64]">Unified (taught-in-completed)</p>
                {data.new_dances.new.length ? <ul className="space-y-1 text-sm">{data.new_dances.new.map((d) => <li key={d.id} className="font-semibold text-[#2D1E18]">{d.name} <span className="text-xs text-[#6E5B51]">· {d.taught_at?.class_name}</span></li>)}</ul> : <p className="text-sm text-[#6E5B51]">None</p>}
              </div>
            </div>
          </div>

          <div>
            <h3 className="mb-2 font-display font-extrabold text-[#2D1E18]">Followed classes</h3>
            <div className="space-y-4">
              {data.followed_classes.map((fc) => (
                <div key={fc.id} className="rounded-xl border border-border bg-card p-3" data-testid={`mp-class-${fc.slug}`}>
                  <p className="mb-2 flex items-center gap-2 font-semibold text-[#2D1E18]">{fc.name} <LevelBadge level={fc.level} />{fc.is_pilot && <span className="inline-flex items-center gap-1 rounded-full bg-[#E7DBF2] px-2 py-0.5 text-[10px] font-bold uppercase text-[#6B3E86]"><FlaskConical size={11} /> pilot</span>}</p>
                  {fc.is_pilot && fc.history ? (
                    <>
                      <p className="mb-2 text-xs text-[#6E5B51]">All history{fc.history.no_terms ? " · (no terms defined — showing complete history)" : ""}{fc.history.fallback ? " · fallback to legacy (sandbox unavailable)" : ""}</p>
                      <div className="grid gap-3 md:grid-cols-2">
                        <div data-testid={`mp-hist-old-${fc.slug}`}><p className="mb-1 text-xs font-bold uppercase text-[#6E5B51]">Legacy</p><MiniTimeline data={fc.history.old} /></div>
                        <div data-testid={`mp-hist-new-${fc.slug}`}><p className="mb-1 text-xs font-bold uppercase text-[#1F6E64]">Unified</p><MiniTimeline data={fc.history.new} /></div>
                      </div>
                    </>
                  ) : <p className="text-sm text-[#6E5B51]">Not in the pilot — member sees the existing legacy experience.</p>}
                </div>
              ))}
              {!data.followed_classes.length && <p className="text-sm text-[#6E5B51]">This member follows no classes.</p>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
